import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import moment from 'moment';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  YAxis,
  XAxis
} from 'recharts';
import { fetchOwnerGroupRetentionExpiry } from '~/api/analytics';
import { OwnerGroupRetentionExpiryRow } from '~/types/analytics';
import retainedStyles from '~/containers/RetainedLeases/RetainedLeases.module.css';

interface Props {
  ownerGroupId: string;
  ownerGroupName: string;
  className?: string;
}

export const OwnerGroupRetentionCard = ({
  ownerGroupId,
  ownerGroupName,
  className
}: Props) => {
  const { data: retentionAggRes } = useQuery(
    ['ownerGroupRetentionExpiry', ownerGroupId],
    async () => {
      try {
        const res = await fetchOwnerGroupRetentionExpiry(ownerGroupId);
        return res.data;
      } catch (e: any) {
        // Backend returns 404 when no buildings/no retention data; treat as "no section"
        if (e?.response?.status === 404) return null;
        throw e;
      }
    },
    { refetchOnWindowFocus: false, enabled: !!ownerGroupId }
  );

  const retentionAggRows = useMemo<OwnerGroupRetentionExpiryRow[]>(() => {
    if (!retentionAggRes) return [];
    return Array.isArray(retentionAggRes)
      ? (retentionAggRes as OwnerGroupRetentionExpiryRow[])
      : ([retentionAggRes] as OwnerGroupRetentionExpiryRow[]);
  }, [retentionAggRes]);

  const latestRetention = useMemo(() => {
    const rowsWithMonth = retentionAggRows.filter((r) => r.month);
    if (!rowsWithMonth.length) return null;
    return [...rowsWithMonth].sort((a, b) => {
      const aTime = moment.utc(a.month!, 'YYYY-MM', true).valueOf();
      const bTime = moment.utc(b.month!, 'YYYY-MM', true).valueOf();
      return bTime - aTime;
    })[0];
  }, [retentionAggRows]);

  const safeRate = (retained: number, expiries: number) => {
    if (!expiries || expiries <= 0) return 0;
    return retained / expiries;
  };

  const retentionChartData = useMemo(() => {
    if (!latestRetention) return [];
    const nonCobuRate = safeRate(
      latestRetention.nonCobuRetained || 0,
      latestRetention.nonCobuExpiries || 0
    );
    const cobuRate = safeRate(
      latestRetention.cobuRetained || 0,
      latestRetention.cobuExpiries || 0
    );
    const totalExpiries =
      latestRetention.totalExpiries ??
      (latestRetention.cobuExpiries || 0) + (latestRetention.nonCobuExpiries || 0);
    const totalRetained =
      latestRetention.totalRetained ??
      (latestRetention.cobuRetained || 0) + (latestRetention.nonCobuRetained || 0);
    const totalRate = safeRate(totalRetained, totalExpiries);

    return [
      { key: 'nonCobu', name: 'Not Cobu Users', value: nonCobuRate, fill: '#FF0000' },
      { key: 'cobu', name: 'Cobu Users', value: cobuRate, fill: '#0B6B00' },
      { key: 'total', name: 'Total', value: totalRate, fill: '#CFCFCF' }
    ];
  }, [latestRetention]);

  const analysisPeriodLabel = useMemo(() => {
    const toYYYYMM = (value: string) => {
      if (/^\d{4}-\d{2}$/.test(value)) return value;
      const parsed = moment.utc(value);
      return parsed.isValid() ? parsed.format('YYYY-MM') : value;
    };

    if (!latestRetention) return null;
    const end = latestRetention.month ? toYYYYMM(latestRetention.month) : null;
    if (!end) return null;

    // If backend doesn't provide a start month, assume a trailing 12-month window.
    const start =
      (latestRetention.dataStartMonth
        ? toYYYYMM(latestRetention.dataStartMonth)
        : moment.utc(end, 'YYYY-MM', true).subtract(12, 'months').format('YYYY-MM')) || null;

    return `Analysis period: ${start} to ${end}`;
  }, [latestRetention]);

  if (!ownerGroupId || !latestRetention || !latestRetention.month) {
    return null;
  }

  return (
    <div className={`${retainedStyles.card} ${className || ''}`.trim()}>
      <div className={retainedStyles.cardTitle}>
        Resident Retention Rate for {ownerGroupName} Portfolio
      </div>
      <div style={{ width: '100%', height: 360 }}>
        <ResponsiveContainer>
          <BarChart
            data={retentionChartData}
            margin={{ top: 44, right: 20, left: 20, bottom: 10 }}
          >
            <CartesianGrid stroke='#D9D9D9' />
            <YAxis
              domain={[0, 1.05]}
              ticks={[0, 0.25, 0.5, 0.75, 1]}
              tickFormatter={(v) => `${Math.round(v * 100)}%`}
            />
            <XAxis dataKey='name' tick={{ fontSize: 18 }} />
            <Bar dataKey='value'>
              {retentionChartData.map((entry) => (
                <Cell key={entry.key} fill={entry.fill} />
              ))}
              <LabelList
                dataKey='value'
                position='top'
                offset={12}
                formatter={(v: any) => `${Math.round(Number(v) * 100)}%`}
                style={{ fill: '#000', fontSize: 22, fontWeight: 600 }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <table className={retainedStyles.summaryTable}>
        <thead>
          <tr>
            <th />
            <th>Not Cobu Users</th>
            <th>Cobu Users</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className={retainedStyles.rowLabel}>Expiring Leases</td>
            <td>{latestRetention.nonCobuExpiries}</td>
            <td>{latestRetention.cobuExpiries}</td>
            <td>
              {latestRetention.totalExpiries ??
                latestRetention.nonCobuExpiries + latestRetention.cobuExpiries}
            </td>
          </tr>
          <tr>
            <td className={retainedStyles.rowLabel}>Retained</td>
            <td>{latestRetention.nonCobuRetained}</td>
            <td>{latestRetention.cobuRetained}</td>
            <td>
              {latestRetention.totalRetained ??
                latestRetention.nonCobuRetained + latestRetention.cobuRetained}
            </td>
          </tr>
        </tbody>
      </table>

      <div className={retainedStyles.ttm}>
        {analysisPeriodLabel || 'Analysis period: —'}
        <div style={{ marginTop: 6, fontSize: 12 }}>
          Buildings with data: {latestRetention.buildingsWithDataCount}/
          {latestRetention.buildingCount} (month {latestRetention.month})
        </div>
        {!latestRetention.dataStartMonth && (
          <div style={{ marginTop: 4, fontSize: 12 }}>
            (Assumes a 12-month window for buildings that have data)
          </div>
        )}
      </div>
      <div className={retainedStyles.analysisDescription}>
        <strong>Analysis Description:</strong> This analysis looks at renewal outcomes for
        residents with expiring leases and compares Cobu users to non-users. A resident is
        considered retained if they have a new lease or move-in within 60 days of lease
        end. Records without an email address or lease end date are excluded. While many
        factors influence renewal decisions, the data shows higher renewal rates among
        residents who use Cobu. This view sums data across all buildings in the owner
        group.
      </div>
    </div>
  );
};
